import React, { useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Truck, CheckCircle, XCircle, Package, ArrowsClockwise, Info, Warning } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const inp = { width: "100%", padding: "9px 11px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 };
const card = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16 };

const Chip = ({ ok, children }) => (
  <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
    background: ok ? "#DCFCE7" : "#FEE2E2", border: `1px solid ${ok ? "#BBF7D0" : "#FECACA"}`,
    color: ok ? "#166534" : "#991B1B", whiteSpace: "nowrap" }}>{children}</span>
);

export default function SapDispatchTest() {
  const [kind, setKind] = useState("so");           // "so" | "po"
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);           // { so_id, header, lines:[{...ship_qty}] }
  const [dispatchNo, setDispatchNo] = useState("");
  const [release, setRelease] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [combining, setCombining] = useState(false);
  const [combineResult, setCombineResult] = useState(null);
  const [refreshingDelivery, setRefreshingDelivery] = useState(false);
  // Combined-dispatch builder (finder + tabulated per-line ship qty)
  const [finderCust, setFinderCust] = useState("");
  const [finderLoading, setFinderLoading] = useState(false);
  const [finderResults, setFinderResults] = useState(null);   // [{so_id, customer, site, line_count}]
  const [builder, setBuilder] = useState([]);   // [{so_id, customer, site, lines:[{...ship_qty}]}]
  const [addSoInput, setAddSoInput] = useState("");
  const [addingSo, setAddingSo] = useState(false);
  const [traceMode, setTraceMode] = useState("so");
  const [traceQuery, setTraceQuery] = useState("");
  const [tracing, setTracing] = useState(false);
  const [traceResults, setTraceResults] = useState(null);
  const [stock, setStock] = useState(null);           // { [so_item]: {available, area, uom, ok} }
  const [stockLoading, setStockLoading] = useState(false);
  // Stranded pick-task inspector (SOs with In-Process lines locking stock — ship the remaining tranche)
  const [strandedLoading, setStrandedLoading] = useState(false);
  const [strandedRows, setStrandedRows] = useState(null);   // [{so_id, customer, in_process, finished, lines:[...]}]
  const [strandedShipping, setStrandedShipping] = useState("");
  const [strandedQty, setStrandedQty] = useState({});       // { `${so_id}::${item}`: ship_qty string }
  // Replenish test stock (admin DoGoodsChangeOfStock helper)
  const [repl, setRepl] = useState({ product: "TEST-FG1", qty: 5, site: "P2", source_area: "P2-FG",
    target_area: "P2-FG", owner: "RT", source_restricted: true, target_restricted: false });
  const [replBusy, setReplBusy] = useState(false);
  const [replResult, setReplResult] = useState(null);
  // Reservations inspector (why on-hand shows available=0 — open pick tasks locking stock)
  const [resv, setResv] = useState(null);
  const [resvBusy, setResvBusy] = useState(false);

  const loadReservations = async () => {
    setResvBusy(true);
    try {
      const r = await api.get("/sap/dispatch-test/reservations", { params: { site: repl.site || "P2" }, timeout: 60000 });
      setResv(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reservations");
    }
    setResvBusy(false);
  };

  const runReplenish = async () => {
    if (!repl.product?.trim() || !(Number(repl.qty) > 0)) { toast.error("Enter a product and qty > 0"); return; }
    setReplBusy(true); setReplResult(null);
    try {
      const r = await api.post("/sap/dispatch-test/replenish-stock",
        { ...repl, qty: Number(repl.qty), confirm: true }, { timeout: 60000 });
      setReplResult(r.data);
      if (r.data?.ok) toast.success(`Stock change posted${r.data.confirmation_id ? ` (${r.data.confirmation_id})` : ""}`);
      else toast.error(r.data?.reason || "Stock change was rejected by SAP");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replenish request failed");
    }
    setReplBusy(false);
  };

  const fetchStock = async (q, k) => {
    setStock(null); setStockLoading(true);
    try {
      const params = k === "po" ? { po: q } : { so: q };
      const r = await api.get("/sap/dispatch-test/stock", { params, timeout: 90000 });
      if (r.data?.ok) {
        const map = {};
        (r.data.stock || []).forEach((s) => { map[s.so_item] = s; });
        setStock(map);
      }
    } catch (e) { /* stock preflight is best-effort; ignore */ }
    setStockLoading(false);
  };

  const load = async () => {
    if (!query.trim()) { toast.error("Enter a Sales Order or PO number"); return; }
    setLoading(true); setData(null); setResult(null); setStock(null);
    try {
      const params = kind === "po" ? { po: query.trim() } : { so: query.trim() };
      const r = await api.get("/sap/dispatch-test/load", { params, timeout: 90000 });
      if (!r.data?.ok) { toast.error(r.data?.reason || "Not found"); setLoading(false); return; }
      const lines = (r.data.lines || []).map((l) => ({ ...l, ship_qty: l.open_qty }));
      setData({ ...r.data, lines });
      if (!lines.length) toast.message("Loaded — but this order has no open lines to ship.");
      else fetchStock(kind === "po" ? query.trim() : (r.data.so_id || query.trim()), kind);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
    setLoading(false);
  };

  const setQty = (i, v) => setData((d) => {
    const lines = d.lines.slice();
    lines[i] = { ...lines[i], ship_qty: v };
    return { ...d, lines };
  });

  const createOutbound = async () => {
    const lines = (data?.lines || [])
      .map((l) => ({ ...l, ship_qty: Number(l.ship_qty) || 0 }))
      .filter((l) => l.ship_qty > 0);
    if (!lines.length) { toast.error("Enter a ship quantity on at least one line"); return; }
    if (!dispatchNo.trim()) { toast.error("Dispatch / Delivery number is required"); return; }
    const over = lines.find((l) => l.ship_qty > l.open_qty);
    if (over) { toast.error(`Qty ${over.ship_qty} exceeds open ${over.open_qty} on ${over.product}`); return; }
    setCreating(true); setResult(null);
    try {
      const body = { so_id: data.so_id, dispatch_number: dispatchNo.trim(), release, lines };
      const start = await api.post("/sap/dispatch-test/create-outbound", body, { timeout: 30000 });
      const jobId = start.data?.job_id;
      if (!jobId) { toast.error("Could not start the job"); setCreating(false); return; }
      // Poll — the SAP work runs server-side to avoid the request timeout.
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await api.get(`/sap/dispatch-test/job/${jobId}`, { timeout: 30000 });
          const j = r.data;
          setResult(j);
          if (j.status === "running" && tries < 200) { setTimeout(poll, 3000); return; }
          setCreating(false);
          if (j.status === "done" && j.ok) toast.success("Outbound delivery(ies) created");
          else if (j.status === "error") toast.error(j.error || "Job failed");
          else toast.warning("Finished with some issues — see results");
        } catch (e) {
          if (tries < 200) { setTimeout(poll, 3000); return; }
          setCreating(false); toast.error("Lost connection while creating");
        }
      };
      setTimeout(poll, 2500);
    } catch (e) {
      setCreating(false);
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  // Combine ALL open lines of this SO into ONE SAP Outbound Delivery (attach → release → GI), no manual SAP UI.
  const combineOutbound = async () => {
    if (!data?.so_id) { toast.error("Load a Sales Order first"); return; }
    if (!dispatchNo.trim()) { toast.error("Dispatch / Delivery number is required"); return; }
    if (!data.combinable_count) {
      toast.error("No combinable lines here — they're already delivered or in process on a pick task. Combine ships Not-Started lines only.");
      return;
    }
    if ((data.sites || []).length > 1) {
      toast.error(`This order has open lines at multiple sites (${data.sites.join(", ")}). A single Outbound Delivery must ship from ONE site.`);
      return;
    }
    setCombining(true); setCombineResult(null);
    try {
      // Ship what the user typed per line (the "Ship" boxes) — single source of truth. Only COMBINABLE
      // (Not-Started) lines go in; In-Process/task lines are excluded (combine can't attach them).
      const qtyByProduct = {};
      let anyPartial = false;
      (data.lines || []).forEach((l) => {
        if (!l.product || !l.combinable) return;
        const q = Number(l.ship_qty);
        const ship = isNaN(q) ? l.open_qty : q;
        qtyByProduct[l.product] = (qtyByProduct[l.product] || 0) + ship;
        if (ship < l.open_qty) anyPartial = true;
      });
      const body = { so_id: data.so_id, dispatch_number: dispatchNo.trim() };
      if (anyPartial) body.qty_by_product = qtyByProduct;   // omit when shipping everything (full delivery)
      const start = await api.post("/sap/dispatch-test/combine", body, { timeout: 30000 });
      const jobId = start.data?.job_id;
      if (!jobId) { toast.error("Could not start the combine job"); setCombining(false); return; }
      pollCombineJob(jobId);
    } catch (e) {
      setCombining(false);
      toast.error(e?.response?.data?.detail || "Combine failed");
    }
  };

  const refreshDelivery = async () => {
    const jobId = combineResult?.job_id;
    if (!jobId) return;
    setRefreshingDelivery(true);
    try {
      const r = await api.post(`/sap/dispatch-test/combine/job/${jobId}/refresh-delivery`, {}, { timeout: 30000 });
      if (r.data?.delivery_id) {
        setCombineResult((prev) => ({ ...prev, delivery_id: r.data.delivery_id, delivery_pending: false }));
        toast.success(`Delivery ${r.data.delivery_id} synced from SAP`);
      } else {
        toast.info("Still syncing from SAP — give it a moment and try again");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh the delivery number");
    } finally {
      setRefreshingDelivery(false);
    }
  };

  const autoFetchDelivery = (jobId, attempt = 0) => {
    // GI already posted — quietly poll SAP for the delivery number so it fills itself in (no clicking).
    // SAP forms the Outbound Delivery in a background run; on P2 (esp. a partial tranche) that can lag
    // several minutes. Poll fast for ~4 min, then slowly up to ~20 min so it resolves without the user
    // babysitting the page. In production the webhook + 2-min backfill sweeper also resolve it server-side.
    if (attempt >= 56) return;                               // 24 fast (10s) + 32 slow (30s) ≈ 20 min
    const delay = attempt === 0 ? 6000 : attempt < 24 ? 10000 : 30000;
    setTimeout(async () => {
      try {
        const r = await api.post(`/sap/dispatch-test/combine/job/${jobId}/refresh-delivery`, {}, { timeout: 30000 });
        if (r.data?.delivery_id) {
          setCombineResult((prev) => (prev && prev.job_id === jobId
            ? { ...prev, delivery_id: r.data.delivery_id, delivery_pending: false } : prev));
          toast.success(`Delivery ${r.data.delivery_id} synced from SAP`);
          return;
        }
      } catch (e) { /* keep retrying quietly */ }
      autoFetchDelivery(jobId, attempt + 1);
    }, delay);
  };

  const pollCombineJob = (jobId, onDone) => {
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/sap/dispatch-test/combine/job/${jobId}`, { timeout: 30000 });
        const j = r.data;
        setCombineResult({ ...j, job_id: jobId });
        if (j.status === "running" && tries < 200) { setTimeout(poll, 3000); return; }
        setCombining(false);
        if (j.status === "done" && j.ok) {
          if (j.delivery_id) toast.success(`Combined delivery ${j.delivery_id} created`);
          else if (j.note) { toast.info("Delivery is materialising in SAP — it will appear automatically"); autoFetchDelivery(jobId); }
          else { toast.success("Shipment posted ✓ — fetching delivery number…"); autoFetchDelivery(jobId); }
        } else if (j.status === "error") toast.error(j.error || "Combine failed");
        else toast.warning("Finished with issues — see steps");
        if (onDone) onDone(j);
      } catch (e) {
        if (tries < 200) { setTimeout(poll, 3000); return; }
        setCombining(false); toast.error("Lost connection while combining");
        if (onDone) onDone(null);
      }
    };
    setTimeout(poll, 2500);
  };

  // ---- Stranded pick-task inspector ----
  const loadStranded = async () => {
    setStrandedLoading(true);
    try {
      const r = await api.get("/sap/dispatch-test/stranded-tasks", { params: { limit: 80 }, timeout: 90000 });
      const rows = r.data?.stranded || [];
      setStrandedRows(rows);
      // Seed each In-Process line's Ship box with its full open qty (default = ship everything remaining).
      const seed = {};
      rows.forEach((s) => (s.lines || []).forEach((l) => { seed[`${s.so_id}::${l.item}`] = String(l.open_qty); }));
      setStrandedQty(seed);
      if (!r.data?.count) toast.info("No stranded pick tasks — every order's lines are Not-Started or Finished");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load stranded pick tasks");
    } finally {
      setStrandedLoading(false);
    }
  };

  const shipRemaining = async (row) => {
    if (strandedShipping) return;
    const soId = row.so_id;
    // Build per-line ship quantities from the Ship boxes. Lines shipped below their open qty send a
    // sub-tranche and keep the balance OPEN (split flag applied server-side); full qty closes the line.
    const qtyByLine = [];
    let anyPartial = false, anyPositive = false, over = null;
    (row.lines || []).forEach((l) => {
      const raw = strandedQty[`${soId}::${l.item}`];
      const q = raw === "" || raw == null ? l.open_qty : Number(raw);
      if (isNaN(q) || q < 0) return;
      if (q > l.open_qty) over = { product: l.product, q, open: l.open_qty };
      if (q > 0) anyPositive = true;
      if (q < l.open_qty) anyPartial = true;
      qtyByLine.push({ product: l.product, open_qty: l.open_qty, ship_qty: q });
    });
    if (over) { toast.error(`Ship qty ${over.q} exceeds open ${over.open} on ${over.product}`); return; }
    if ((row.lines || []).length && !anyPositive) { toast.error("Enter a Ship qty of at least 1 on a line"); return; }
    setStrandedShipping(soId);
    setCombining(true); setCombineResult(null);
    try {
      const body = { so_id: soId, ship_remaining: true };
      // Per-line sub-tranche only when we resolved the task lines AND at least one is a partial.
      // If no lines were resolvable, fall back to shipping the full remaining tranche (old behavior).
      if ((row.lines || []).length && anyPartial) body.qty_by_line = qtyByLine;
      const start = await api.post("/sap/dispatch-test/combine", body, { timeout: 30000 });
      const jobId = start.data?.job_id;
      if (!jobId) { toast.error("Could not start the ship job"); setCombining(false); setStrandedShipping(""); return; }
      toast.info(`Shipping ${anyPartial ? "sub-tranche" : "remaining tranche"} of SO ${soId}…`);
      pollCombineJob(jobId, () => { setStrandedShipping(""); loadStranded(); });
    } catch (e) {
      setCombining(false); setStrandedShipping("");
      toast.error(e?.response?.data?.detail || "Ship-remaining failed");
    }
  };

  // ---- Combined-dispatch builder ----
  const findCombinable = async () => {
    setFinderLoading(true);
    try {
      const r = await api.get("/sap/dispatch-test/combinable", {
        params: { customer: finderCust || undefined, limit: 200 }, timeout: 60000 });
      setFinderResults(r.data?.sales_orders || []);
      if (!r.data?.count) toast.info("No combinable Sales Orders found in the recent window");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Finder failed");
    } finally { setFinderLoading(false); }
  };

  const addSoToBuilder = async (soId) => {
    const sid = String(soId || "").trim();
    if (!sid) return;
    if (builder.some((b) => b.so_id === sid)) { toast.info(`SO ${sid} already added`); return; }
    setAddingSo(true);
    try {
      const r = await api.get("/sap/dispatch-test/load", { params: { so: sid }, timeout: 60000 });
      if (!r.data?.ok) { toast.error(r.data?.reason || `Could not load SO ${sid}`); return; }
      const lines = (r.data.lines || []).filter((l) => l.combinable);
      if (!lines.length) { toast.error(`SO ${sid} has no combinable (Not-Started) lines`); return; }
      const site = (r.data.sites || [])[0] || lines[0].site || "";
      const existingSite = builder[0]?.site;
      if (existingSite && site && site !== existingSite) {
        toast.error(`SO ${sid} ships from ${site}, but the builder is set to ${existingSite}. A single delivery must ship from ONE site.`);
        return;
      }
      setBuilder((prev) => [...prev, {
        so_id: sid, customer: lines[0].customer || r.data.header?.customer || "", site,
        lines: lines.map((l) => ({ ...l, ship_qty: l.open_qty })),
      }]);
      setAddSoInput("");
      toast.success(`Added SO ${sid} (${lines.length} line${lines.length > 1 ? "s" : ""})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not add SO ${sid}`);
    } finally { setAddingSo(false); }
  };

  const removeSoFromBuilder = (sid) => setBuilder((prev) => prev.filter((b) => b.so_id !== sid));
  const setBuilderQty = (sid, idx, val) => setBuilder((prev) => prev.map((b) =>
    b.so_id !== sid ? b : { ...b, lines: b.lines.map((l, i) => i === idx ? { ...l, ship_qty: val } : l) }));

  const runBuilderCombine = async () => {
    if (builder.length < 1) { toast.error("Add at least one Sales Order"); return; }
    if (!dispatchNo.trim()) { toast.error("Dispatch / Delivery number is required"); return; }
    const sites = [...new Set(builder.map((b) => b.site).filter(Boolean))];
    if (sites.length > 1) { toast.error(`Multiple sites (${sites.join(", ")}). All SOs must ship from ONE site.`); return; }
    const qty_by_line = [];
    let over = null;
    builder.forEach((b) => b.lines.forEach((l) => {
      const q = Number(l.ship_qty);
      if (isNaN(q) || q <= 0) return;
      if (q > l.open_qty) over = l;
      qty_by_line.push({ so_id: b.so_id, product: l.product, open_qty: l.open_qty, ship_qty: q });
    }));
    if (over) { toast.error(`Ship qty ${over.ship_qty} exceeds open ${over.open_qty} on ${over.product}`); return; }
    if (!qty_by_line.length) { toast.error("Enter a ship qty on at least one line"); return; }
    // Stock preflight: aggregate ship demand per product vs available — a short line dumps the Goods Issue.
    const shipByProduct = {}, availByProduct = {};
    builder.forEach((b) => b.lines.forEach((l) => {
      const q = Number(l.ship_qty) || 0;
      shipByProduct[l.product] = (shipByProduct[l.product] || 0) + q;
      if (l.available != null) availByProduct[l.product] = l.available;
    }));
    const short = Object.keys(shipByProduct).filter((p) => availByProduct[p] != null && shipByProduct[p] > availByProduct[p]);
    if (short.length) {
      toast.error(`Not enough stock at ${sites[0] || "the site"} for ${short.join(", ")} — the Goods Issue would fail. Lower the ship qty or ship from a site with stock.`);
      return;
    }
    setCombining(true); setCombineResult(null);
    try {
      const body = { so_ids: builder.map((b) => b.so_id), qty_by_line, dispatch_number: dispatchNo.trim() };
      const start = await api.post("/sap/dispatch-test/combine", body, { timeout: 30000 });
      const jobId = start.data?.job_id;
      if (!jobId) { toast.error("Could not start the combine"); setCombining(false); return; }
      pollCombineJob(jobId);
    } catch (e) {
      setCombining(false);
      toast.error(e?.response?.data?.detail || "Combine failed");
    }
  };

  const runTrace = async () => {
    const query = (traceQuery || "").trim();
    if (!query) { toast.error(`Enter a ${traceMode === "so" ? "Sales Order" : "Delivery"} number`); return; }
    setTracing(true); setTraceResults(null);
    try {
      const params = traceMode === "so" ? { so: query } : { delivery: query };
      const r = await api.get("/sap/dispatch-test/trace", { params, timeout: 30000 });
      setTraceResults(r.data);
      if (!r.data?.count) toast.info("No combined shipments found for that search");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trace lookup failed");
    } finally {
      setTracing(false);
    }
  };

  return (
    <Layout breadcrumb={["Setup", "SAP Dispatch Push Test"]}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1100 }}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 22, fontWeight: 800, margin: 0 }}>
            <Truck size={24} weight="duotone" /> SAP Dispatch Push — Test
          </h1>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
            Load a Sales Order (or a PO that's already in SAP), enter the quantity to ship on each line and a
            dispatch number, then create the SAP Outbound Delivery and read back its number. Full-quantity
            lines are stamped with the DispatchNumber and grouped; partial lines ship a tranche.
          </p>
        </div>

        {/* Search */}
        <div style={{ ...card, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select data-testid="dt-kind" value={kind} onChange={(e) => setKind(e.target.value)}
                  style={{ ...inp, width: 150 }}>
            <option value="so">Sales Order #</option>
            <option value="po">Customer PO #</option>
          </select>
          <input data-testid="dt-query" style={{ ...inp, width: 260 }} value={query}
                 placeholder={kind === "po" ? "e.g. OP49375" : "e.g. 11860"}
                 onChange={(e) => setQuery(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()} />
          <button data-testid="dt-search-btn" className="jde-btn jde-btn-primary" onClick={load} disabled={loading}>
            <MagnifyingGlass size={15} weight="bold" /> {loading ? "Searching…" : "Search SAP"}
          </button>
        </div>

        {/* Reservations inspector — shows which open pick tasks lock stock (why available can be 0) */}
        <details style={{ ...card, padding: 0 }} data-testid="dt-reservations-card">
          <summary style={{ cursor: "pointer", padding: 16, fontSize: 15, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
            <Warning size={18} weight="fill" color="#b45309" /> Stock reservations — free locked test stock
          </summary>
          <div style={{ padding: "0 16px 16px" }}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
              On-hand stock can show <b>available = 0</b> because open pick tasks have it <b>reserved</b>. ByD has no
              web-service to release a reservation — to free it, <b>reject</b> the listed warehouse requests in
              <b> Outbound Logistics Control → Delivery Proposals / Requests</b> (or ship them). This list shows exactly which to reject.
            </div>
            <button data-testid="dt-resv-load" className="jde-btn jde-btn-secondary" onClick={loadReservations} disabled={resvBusy}>
              {resvBusy ? "Checking…" : "Check reservations at P2"}
            </button>
            {resv && (
              <div style={{ marginTop: 14 }} data-testid="dt-resv-result">
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "4px 16px", fontSize: 13, marginBottom: 12 }}>
                  <span style={{ fontWeight: 800 }}>Product</span>
                  <span style={{ fontWeight: 800, textAlign: "right" }}>On-hand</span>
                  <span style={{ fontWeight: 800, textAlign: "right" }}>Reserved</span>
                  <span style={{ fontWeight: 800, textAlign: "right" }}>Available</span>
                  {Object.entries(resv.available || {}).map(([p, v]) => (
                    <React.Fragment key={p}>
                      <span data-testid={`dt-resv-prod-${p}`}>{p}</span>
                      <span style={{ textAlign: "right" }}>{v.on_hand}</span>
                      <span style={{ textAlign: "right", color: "#b45309", fontWeight: 700 }}>{v.reserved}</span>
                      <span style={{ textAlign: "right", color: (v.available || 0) > 0 ? "#166534" : "#991B1B", fontWeight: 800 }}>{v.available}</span>
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", marginBottom: 6 }}>
                  {resv.task_count} open task(s) reserving stock — reject these requests to free it:
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: "#f8fafc", textAlign: "left" }}>
                      <th style={{ padding: "6px 10px" }}>Task ID</th><th style={{ padding: "6px 10px" }}>SO / ref</th>
                      <th style={{ padding: "6px 10px" }}>Product</th><th style={{ padding: "6px 10px", textAlign: "right" }}>Qty</th>
                    </tr></thead>
                    <tbody>
                      {(resv.tasks || []).map((t, i) => (
                        <tr key={i} data-testid={`dt-resv-task-${t.task_id}-${i}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "6px 10px", fontFamily: "Inter, sans-serif" }}>{t.task_id}</td>
                          <td style={{ padding: "6px 10px" }}>{t.reference_id || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                          <td style={{ padding: "6px 10px" }}>{t.product}</td>
                          <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>{t.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </details>

        {/* Replenish test stock — admin DoGoodsChangeOfStock helper (transfer between areas / free restricted) */}
        <details style={{ ...card, padding: 0 }} data-testid="dt-replenish-card">
          <summary style={{ cursor: "pointer", padding: 16, fontSize: 15, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
            <Package size={18} weight="fill" color="#0f766e" /> Replenish test stock (admin)
          </summary>
          <div style={{ padding: "0 16px 16px" }}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
              Posts a SAP <b>DoGoodsChangeOfStock</b>. This <b>transfers</b> stock between logistics areas or
              flips restricted&nbsp;→&nbsp;unrestricted — it cannot create net-new quantity (to add quantity you need a goods receipt).
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Product
                <select data-testid="dt-repl-product" style={{ ...inp, marginTop: 4 }} value={repl.product}
                        onChange={(e) => setRepl({ ...repl, product: e.target.value })}>
                  <option>TEST-FG1</option><option>TEST-FG2</option><option>TEST-FG3</option>
                </select>
              </label>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Qty
                <input data-testid="dt-repl-qty" type="number" min="1" step="1" style={{ ...inp, marginTop: 4 }}
                       value={repl.qty} onChange={(e) => setRepl({ ...repl, qty: e.target.value })} />
              </label>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Site
                <input data-testid="dt-repl-site" style={{ ...inp, marginTop: 4 }} value={repl.site}
                       onChange={(e) => setRepl({ ...repl, site: e.target.value })} />
              </label>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Owner
                <input data-testid="dt-repl-owner" style={{ ...inp, marginTop: 4 }} value={repl.owner}
                       onChange={(e) => setRepl({ ...repl, owner: e.target.value })} />
              </label>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Source area
                <input data-testid="dt-repl-source" style={{ ...inp, marginTop: 4 }} value={repl.source_area}
                       onChange={(e) => setRepl({ ...repl, source_area: e.target.value })} />
              </label>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Target area
                <input data-testid="dt-repl-target" style={{ ...inp, marginTop: 4 }} value={repl.target_area}
                       onChange={(e) => setRepl({ ...repl, target_area: e.target.value })} />
              </label>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input data-testid="dt-repl-src-restricted" type="checkbox" checked={repl.source_restricted}
                       onChange={(e) => setRepl({ ...repl, source_restricted: e.target.checked })} />
                Source is restricted (free blocked stock)
              </label>
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input data-testid="dt-repl-tgt-restricted" type="checkbox" checked={repl.target_restricted}
                       onChange={(e) => setRepl({ ...repl, target_restricted: e.target.checked })} />
                Target is restricted
              </label>
              <button data-testid="dt-repl-run" className="jde-btn jde-btn-primary" onClick={runReplenish} disabled={replBusy}>
                {replBusy ? "Posting…" : "Post change of stock"}
              </button>
            </div>
            {replResult && (
              <div data-testid="dt-repl-result" style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13,
                background: replResult.ok ? "#F0FDF4" : "#FEF2F2",
                border: `1px solid ${replResult.ok ? "#BBF7D0" : "#FECACA"}`, color: replResult.ok ? "#166534" : "#991B1B" }}>
                {replResult.ok
                  ? <><CheckCircle size={16} weight="fill" /> Stock change posted{replResult.confirmation_id ? ` — confirmation ${replResult.confirmation_id}` : ""}.</>
                  : <><XCircle size={16} weight="fill" /> {replResult.reason || "SAP rejected the stock change."}</>}
              </div>
            )}
          </div>
        </details>

        {/* Stranded pick-task inspector — SOs with In-Process lines locking stock (ship the remaining tranche) */}
        <div style={card} data-testid="dt-stranded-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800 }}>
                <Package size={18} weight="duotone" /> Stranded pick tasks
              </div>
              <p style={{ color: "#64748b", fontSize: 12, margin: "4px 0 0" }}>
                Sales Orders with lines <b>In Process</b> on an open pick task (Goods Issue never confirmed) and
                <b> no Not-Started lines left</b> — their stock stays reserved. Ship the remaining tranche as one
                Outbound Delivery, no SAP UI.
              </p>
            </div>
            <button data-testid="dt-stranded-refresh" className="jde-btn jde-btn-primary"
                    onClick={loadStranded} disabled={strandedLoading}>
              <ArrowsClockwise size={14} weight="bold" /> {strandedLoading ? "Scanning…" : "Scan stranded tasks"}
            </button>
          </div>
          {strandedRows && (
            <div data-testid="dt-stranded-results" style={{ marginTop: 12 }}>
              {strandedRows.length === 0 ? (
                <div style={{ padding: 12, fontSize: 13, color: "#64748b", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                  No stranded pick tasks found in the recent window.
                </div>
              ) : (
                strandedRows.map((s) => (
                  <div key={s.so_id} data-testid={`dt-stranded-row-${s.so_id}`}
                       style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                      <span style={{ fontWeight: 800 }}>SO {s.so_id}</span>
                      <span style={{ fontSize: 12, color: "#64748b" }}>{s.customer || "—"}</span>
                      <Chip ok={false}>{s.in_process} in process</Chip>
                      {s.finished ? <span style={{ fontSize: 11, color: "#64748b" }}>{s.finished} finished</span> : null}
                    </div>
                    {(s.lines || []).map((l) => (
                      <div key={l.item} data-testid={`dt-stranded-line-${s.so_id}-${l.item}`}
                           style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                                    gap: 12, padding: "6px 0", borderTop: "1px solid #F1F5F9" }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 700 }}>#{l.item} · {l.product}</span>
                          <span style={{ fontSize: 12, color: "#64748b", marginLeft: 8 }}>
                            {l.description || ""}{l.site ? ` · ${l.site}` : ""}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, whiteSpace: "nowrap" }}>
                          <span style={{ fontSize: 12, color: "#64748b" }}>Open <b>{l.open_qty} {l.unit}</b></span>
                          <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>Ship</label>
                          <input data-testid={`dt-stranded-qty-${s.so_id}-${l.item}`} type="number" min="0"
                                 max={l.open_qty} step="1"
                                 value={strandedQty[`${s.so_id}::${l.item}`] ?? ""}
                                 onChange={(e) => setStrandedQty((p) => ({ ...p, [`${s.so_id}::${l.item}`]: e.target.value }))}
                                 style={{ ...inp, width: 80 }} />
                        </div>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                      <button data-testid={`dt-stranded-ship-${s.so_id}`} className="jde-btn jde-btn-primary"
                              onClick={() => shipRemaining(s)}
                              disabled={!!strandedShipping}>
                        <Truck size={13} weight="bold" />
                        {strandedShipping === s.so_id ? " Shipping…" : " Ship tranche → 1 Delivery"}
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                      Ship the full open qty to close a line, or less to send another sub-tranche and keep the balance open.
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Loaded order */}
        {data && (
          <div style={card} data-testid="dt-order">
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                Sales Order {data.so_id}
                {data.resolved_from && <span style={{ color: "#64748b", fontWeight: 500 }}> · from {data.resolved_from}</span>}
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 12, color: "#475569", flexWrap: "wrap" }}>
                <span>Delivery: <b>{data.header?.delivery_status || "—"}</b></span>
                <span>Invoice: <b>{data.header?.invoice_status || "—"}</b></span>
                <span>Sales Unit: <b>{data.header?.sales_org || "—"}</b></span>
              </div>
            </div>

            {data.lines.length === 0 ? (
              <div style={{ color: "#92400E", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: 12, fontSize: 13 }}>
                No open lines on this order (already delivered or nothing shippable).
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {data.lines.map((l, i) => {
                    const s = stock && stock[l.so_item];
                    const need = Number(l.ship_qty) || l.open_qty;
                    const ok = s && s.site && s.available >= need && need > 0;
                    return (
                      <div key={l.object_id} data-testid={`dt-line-${l.so_item}`}
                           style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>
                              <span style={{ color: "#64748b" }}>#{l.so_item}</span> · {l.product || <span style={{ color: "#94a3b8" }}>—</span>}
                              {l.on_open_task && (
                                <span data-testid={`dt-ontask-${l.so_item}`}
                                      title="Remaining tranche on an open pick task from an earlier partial ship — shippable via single-line dispatch, NOT combinable"
                                      style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#9a3412",
                                               background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 999, padding: "1px 8px" }}>
                                  ON OPEN TASK · NOT COMBINABLE
                                </span>
                              )}
                              {l.site && (
                                <span data-testid={`dt-site-${l.so_item}`}
                                      title="Ship-from site"
                                      style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#0369a1",
                                               background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 999, padding: "1px 8px" }}>
                                  {l.site}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>{l.description || "—"}</div>
                          </div>
                          <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase" }}>Open</div>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{l.open_qty} {l.unit}</div>
                          </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                                      gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                          <div data-testid={`dt-stock-${l.so_item}`} style={{ minWidth: 0 }}>
                            {stockLoading ? (
                              <span style={{ color: "#94a3b8", fontSize: 11 }}>checking stock…</span>
                            ) : s ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <Chip ok={ok}>{ok ? "OK" : (s.site ? "SHORT" : "NO SITE")}</Chip>
                                <span style={{ fontSize: 11, color: "#64748b" }}
                                      title={typeof s.on_hand === "number" ? `${s.on_hand} on-hand − ${s.reserved} reserved (open tasks)` : ""}>
                                  {s.available} {s.uom} free
                                  {typeof s.reserved === "number" && s.reserved > 0 ? ` · ${s.on_hand} on-hand` : ""}
                                  {s.area ? ` · ${s.area}` : ""}
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: "#cbd5e1", fontSize: 11 }}>—</span>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>Ship</label>
                            <input data-testid={`dt-qty-${l.so_item}`} type="number" min="0" max={l.open_qty} step="1"
                                   style={{ ...inp, width: 90, padding: "6px 8px" }} value={l.ship_qty}
                                   onChange={(e) => setQty(i, e.target.value)} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Stock preflight summary — safe-to-combine indicator */}
                {stock && Object.keys(stock).length > 0 && (() => {
                  const items = data.lines.map((l) => {
                    const s = stock[l.so_item]; if (!s) return null;
                    const need = Number(l.ship_qty) || l.open_qty;
                    return { ...s, ok: s.site && s.available >= need && need > 0 };
                  }).filter(Boolean);
                  const shortN = items.filter((s) => !s.ok).length;
                  const allOk = shortN === 0 && items.length > 0;
                  return (
                    <div data-testid="dt-stock-preflight" style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10,
                      display: "flex", alignItems: "center", gap: 10, fontSize: 13,
                      background: allOk ? "#F0FDF4" : "#FFFBEB",
                      border: `1px solid ${allOk ? "#BBF7D0" : "#FDE68A"}`,
                      color: allOk ? "#166534" : "#92400E" }}>
                      {allOk
                        ? <><CheckCircle size={18} weight="fill" color="#16a34a" />
                            <b>Stock OK</b> — every line is available at its ship-from site. Safe to combine into one delivery.</>
                        : <><XCircle size={18} weight="fill" color="#d97706" />
                            <b>{shortN} line(s) short at site</b> — SAP will split the shortfall into an unsourced portion and the
                            Goods Issue will fail. Reduce the ship qty or replenish before combining.</>}
                    </div>
                  );
                })()}

                {/* Dispatch + action */}
                <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap", marginTop: 16,
                              borderTop: "1px solid #E2E8F0", paddingTop: 16 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
                      Dispatch / Delivery number <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input data-testid="dt-dispatch-no" style={{ ...inp, width: 220 }} value={dispatchNo}
                           placeholder="required · e.g. TRUCK-2026-001" onChange={(e) => setDispatchNo(e.target.value)} />
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, paddingBottom: 9 }}>
                    <input data-testid="dt-release" type="checkbox" checked={release}
                           onChange={(e) => setRelease(e.target.checked)} />
                    Post goods issue (release stock now)
                  </label>
                  <button data-testid="dt-create-btn" className="jde-btn jde-btn-primary"
                          style={{ marginLeft: "auto", padding: "10px 18px" }}
                          title={!dispatchNo.trim() ? "Enter a Dispatch / Delivery number first" : "Create the SAP Outbound Delivery"}
                          onClick={createOutbound} disabled={creating || !dispatchNo.trim()}>
                    <Package size={16} weight="bold" /> {creating ? "Creating in SAP…" : "Create Outbound"}
                  </button>
                  <button data-testid="dt-combine-btn" className="jde-btn"
                          style={{ padding: "10px 18px", background: "#0f766e", color: "#fff", border: "1px solid #0f766e" }}
                          onClick={combineOutbound} disabled={combining || !dispatchNo.trim()}
                          title="Attach every open line to one warehouse request, release it, and post goods issue — one SAP Outbound Delivery. Ships the qty in each line's Ship box; lines shipped below their open qty keep the balance open. No manual SAP work.">
                    <Truck size={16} weight="bold" /> {combining ? "Combining in SAP…" : "Combine → 1 Delivery"}
                  </button>
                </div>
                <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
                  Full-quantity lines create a draft delivery stamped with the dispatch number (grouped);
                  they only ship when “Post goods issue” is ticked. Partial quantities always require “Post
                  goods issue” (SAP can’t stamp a dispatch number on a partial delivery).
                </p>
                <p style={{ fontSize: 11, color: "#0f766e", marginTop: 4, fontWeight: 600 }}>
                  “Combine → 1 Delivery” ships EVERY line of this order together as ONE SAP Outbound Delivery
                  (attach all lines → release → goods issue), using the qty in each line’s <b>Ship</b> box.
                  Ship less than the open qty on any line to send a tranche and keep the order open — no SAP screen needed.
                </p>
              </>
            )}
          </div>
        )}

        {/* Combine result */}
        {combineResult && (
          <div style={card} data-testid="dt-combine-result">
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              {combineResult.status === "running"
                ? <><span className="dt-spin" style={{ width: 14, height: 14, border: "2px solid #cbd5e1", borderTopColor: "#0f766e", borderRadius: "50%", display: "inline-block", animation: "dtspin 0.7s linear infinite" }} /> Combining in SAP…</>
                : combineResult.delivery_id
                  ? <><CheckCircle size={18} weight="fill" color="#16a34a" /> Combined delivery created</>
                  : combineResult.ok
                    ? <><span className="dt-spin" style={{ width: 14, height: 14, border: "2px solid #FDE68A", borderTopColor: "#B45309", borderRadius: "50%", display: "inline-block", animation: "dtspin 0.7s linear infinite" }} /> <span style={{ color: "#B45309" }}>Delivery materialising in SAP…</span></>
                    : <><XCircle size={18} weight="fill" color="#dc2626" /> Combine finished with issues</>}
            </div>
            {combineResult.delivery_id && (
              <div data-testid="dt-combine-delivery" style={{ fontSize: 14, marginBottom: 8 }}>
                SAP Customer Outbound: <b style={{ color: "#0f766e" }}>
                  {(combineResult.deliveries && combineResult.deliveries.length > 1)
                    ? combineResult.deliveries.join(", ")
                    : combineResult.delivery_id}</b>
                {(combineResult.so_ids?.length || combineResult.so_id) && (
                  <span data-testid="dt-combine-so" style={{ marginLeft: 8, fontWeight: 700, color: "#1d4ed8" }}>
                    · SO {combineResult.so_ids?.length ? combineResult.so_ids.join(", ") : combineResult.so_id}
                  </span>
                )}
                <span style={{ color: "#64748b" }}>
                  {" — "}
                  {(combineResult.deliveries && combineResult.deliveries.length > 1)
                    ? `${combineResult.deliveries.length} deliveries · `
                    : "one delivery · "}
                  shipped {(combineResult.ship_summary?.lines || []).reduce((a, l) => a + (Number(l.shipped) || 0), 0)} EA
                </span>
              </div>
            )}
            {combineResult.dispatch_number && (
              <div data-testid="dt-combine-stamp" style={{ fontSize: 13, marginBottom: 8, padding: "8px 12px",
                borderRadius: 8, background: (combineResult.stamps || []).length && (combineResult.stamps || []).every((s) => s.ok)
                  ? "#F0FDF4" : "#FEF2F2",
                border: `1px solid ${(combineResult.stamps || []).length && (combineResult.stamps || []).every((s) => s.ok) ? "#BBF7D0" : "#FECACA"}` }}>
                {(combineResult.stamps || []).length && (combineResult.stamps || []).every((s) => s.ok)
                  ? <><CheckCircle size={15} weight="fill" color="#16a34a" /> <b>DispatchNumber {combineResult.dispatch_number}</b> stamped on {combineResult.stamps.length} deliver{combineResult.stamps.length > 1 ? "ies" : "y"} ✓</>
                  : <><Info size={15} weight="fill" color="#D97706" /> DispatchNumber <b>{combineResult.dispatch_number}</b> — stamped {(combineResult.stamps || []).filter((s) => s.ok).length}/{(combineResult.stamps || []).length || "…"} (resolves once the delivery materialises)</>}
              </div>
            )}
            {combineResult.ok && !combineResult.delivery_id && (
              <div data-testid="dt-combine-pending" style={{ fontSize: 13, marginBottom: 8, padding: "10px 14px",
                background: combineResult.note ? "#FFFBEB" : "#F0FDF4",
                border: `1px solid ${combineResult.note ? "#FDE68A" : "#BBF7D0"}`, borderRadius: 8,
                color: combineResult.note ? "#92400E" : "#166534" }}>
                <div style={{ fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", gap: 7 }}>
                  {combineResult.note
                    ? <><Info size={18} weight="fill" color="#D97706" /> Customer Outbound materialising in SAP{combineResult.site ? ` (site ${combineResult.site})` : ""}{(combineResult.so_ids?.length || combineResult.so_id) ? ` · SO ${combineResult.so_ids?.length ? combineResult.so_ids.join(", ") : combineResult.so_id}` : ""}</>
                    : <><CheckCircle size={18} weight="fill" color="#16a34a" /> Shipment posted successfully — Goods Issue confirmed in SAP{(combineResult.so_ids?.length || combineResult.so_id) ? ` · SO ${combineResult.so_ids?.length ? combineResult.so_ids.join(", ") : combineResult.so_id}` : ""}</>}
                </div>
                <div style={{ marginTop: 4, display: "flex", alignItems: "flex-start", gap: 7, color: combineResult.note ? "#B45309" : "#3f6212" }}>
                  <span className="dt-spin" style={{ width: 12, height: 12, marginTop: 2, border: `2px solid ${combineResult.note ? "#FCD34D" : "#86efac"}`,
                    borderTopColor: combineResult.note ? "#92400E" : "#166534", borderRadius: "50%", display: "inline-block", animation: "dtspin 0.7s linear infinite" }} />
                  <span>{combineResult.note
                    || "Goods Issue is posted ✓ — SAP is now forming the Outbound Delivery document in a background run. On P2 (especially a partial tranche) this can take a few minutes. The number appears here automatically — it’s safe to leave this page; it also syncs server-side. Hit “Check now” to fetch it immediately."}</span>
                </div>
                <div style={{ marginTop: 8 }}>
                  <button data-testid="dt-refresh-delivery-btn" onClick={refreshDelivery} disabled={refreshingDelivery}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700,
                      padding: "6px 12px", borderRadius: 8, border: `1px solid ${combineResult.note ? "#D97706" : "#16a34a"}`,
                      background: refreshingDelivery ? (combineResult.note ? "#FEF3C7" : "#dcfce7") : "#fff",
                      color: combineResult.note ? "#92400E" : "#166534",
                      cursor: refreshingDelivery ? "default" : "pointer" }}>
                    {refreshingDelivery
                      ? <><span className="dt-spin" style={{ width: 12, height: 12, border: "2px solid #FCD34D", borderTopColor: "#92400E", borderRadius: "50%", display: "inline-block", animation: "dtspin 0.7s linear infinite" }} /> Checking…</>
                      : <><ArrowsClockwise size={14} weight="bold" /> Check now</>}
                  </button>
                </div>
              </div>
            )}
            {combineResult.error && (
              <div style={{ fontSize: 12, color: "#991B1B", marginBottom: 8 }}>{combineResult.error}</div>
            )}
            {combineResult.ship_summary?.short && (
              <div data-testid="dt-unsourced-warning" style={{ fontSize: 13, marginBottom: 10, padding: "10px 14px",
                background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, color: "#92400E" }}>
                <div style={{ fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", gap: 7 }}>
                  <Info size={18} weight="fill" color="#D97706" />
                  {combineResult.ship_summary.short_reason === "no_open_qty"
                    ? "Part of this order did not ship — no open quantity left"
                    : combineResult.ship_summary.short_reason === "sap_partial_confirm"
                    ? "Part of this order did not ship — stock is still open on the task"
                    : "Part of this order stayed OPEN — stock was short"}
                </div>
                <div style={{ marginTop: 6, color: "#B45309" }}>
                  {combineResult.ship_summary.short_reason === "no_open_qty" ? (
                    <>These line(s){(combineResult.ship_summary.no_open_products || []).length
                      ? ` (${(combineResult.ship_summary.no_open_products).join(", ")})` : ""} had no open
                      quantity left to pick — they may already be delivered on an earlier dispatch. Use the
                      Delivery ⇄ SO trace to confirm what already shipped.</>
                  ) : combineResult.ship_summary.short_reason === "sap_partial_confirm" ? (
                    <>SAP accepted the Goods Issue but only shipped part of the request — the balance of
                    {(combineResult.ship_summary.stayed_open_products || []).length
                      ? ` ${(combineResult.ship_summary.stayed_open_products).join(", ")}` : " some lines"} is
                    STILL OPEN on the task (not delivered, not lost). Click <b>Create combined delivery</b> again
                    to ship the remainder.</>
                  ) : (
                    <>SAP could only source part of {(combineResult.ship_summary.unsourced_products || []).join(", ") || "some lines"} at
                    {combineResult.site ? ` site ${combineResult.site}` : " the ship-from site"}. The available quantity shipped on this
                    delivery; the rest remains open and will ship automatically on the next combine once stock is replenished.</>
                  )}
                </div>
                <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: "2px 16px", fontSize: 12 }}>
                  <span style={{ fontWeight: 700 }}>Product</span>
                  <span style={{ fontWeight: 700, textAlign: "right" }}>Requested</span>
                  <span style={{ fontWeight: 700, textAlign: "right" }}>Shipped</span>
                  <span style={{ fontWeight: 700, textAlign: "right" }}>Left open</span>
                  {(combineResult.ship_summary.lines || []).map((l, i) => (
                    <React.Fragment key={i}>
                      <span data-testid={`dt-unsourced-prod-${l.product}`}>{l.product}</span>
                      <span style={{ textAlign: "right" }}>{l.requested ?? "—"}</span>
                      <span style={{ textAlign: "right", color: "#166534", fontWeight: 700 }}>{l.shipped}</span>
                      <span style={{ textAlign: "right", color: (l.open || 0) > 0 ? "#b45309" : "#64748b", fontWeight: (l.open || 0) > 0 ? 800 : 400 }}>{l.open ?? "—"}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(combineResult.steps || []).map((s, i) => (
                <span key={i} data-testid={`dt-combine-step-${i}`} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6,
                  background: "#F1F5F9", border: "1px solid #E2E8F0", color: "#334155" }}>
                  {s.step}{s.item ? ` #${s.item}` : ""}{s.ok === false ? " ✕" : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div style={card} data-testid="dt-result">
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              {result.status === "running"
                ? <><span className="dt-spin" style={{ width: 14, height: 14, border: "2px solid #cbd5e1", borderTopColor: "#2563eb", borderRadius: "50%", display: "inline-block", animation: "dtspin 0.7s linear infinite" }} /> Creating in SAP… ({result.done || 0}/{result.total || 0})</>
                : result.ok
                  ? <><CheckCircle size={18} weight="fill" color="#16a34a" /> Outbound created</>
                  : <><XCircle size={18} weight="fill" color="#dc2626" /> {result.error ? "Job failed" : "Finished with issues"}</>}
              {result.dispatch_number ? <span style={{ color: "#64748b", fontWeight: 600 }}>· DispatchNumber {result.dispatch_number}</span> : null}
            </div>
            {result.error && <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 8 }}>{result.error}</div>}
            <style>{"@keyframes dtspin{to{transform:rotate(360deg)}}"}</style>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#64748b", fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ padding: "6px 8px" }}>Item</th>
                  <th style={{ padding: "6px 8px" }}>Product</th>
                  <th style={{ padding: "6px 8px" }}>Qty</th>
                  <th style={{ padding: "6px 8px" }}>Mode</th>
                  <th style={{ padding: "6px 8px" }}>SAP Delivery #</th>
                  <th style={{ padding: "6px 8px" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(result.results || []).map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }} data-testid={`dt-result-${r.so_item}`}>
                    <td style={{ padding: "8px 8px", fontWeight: 700 }}>{r.so_item}</td>
                    <td style={{ padding: "8px 8px" }}>{r.product}</td>
                    <td style={{ padding: "8px 8px" }}>{r.ship_qty}</td>
                    <td style={{ padding: "8px 8px" }}>{r.mode}</td>
                    <td style={{ padding: "8px 8px", fontWeight: 800, color: "#1E40AF" }}>
                      {r.sap_delivery_id || <span style={{ color: "#94a3b8", fontWeight: 400 }}>—</span>}
                    </td>
                    <td style={{ padding: "8px 8px" }}>
                      <Chip ok={r.ok}>{r.ok ? (r.released ? "shipped" : "draft") : "failed"}</Chip>
                      {r.stamped ? <span style={{ marginLeft: 6, fontSize: 11, color: "#16a34a" }}>stamped</span> : null}
                      {r.error && <div style={{ fontSize: 11, color: "#b45309", marginTop: 3 }}>{r.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(result.group || []).length > 0 && (
              <div style={{ marginTop: 12, fontSize: 12, color: "#475569" }}>
                Grouped under DispatchNumber <b>{result.dispatch_number}</b>:{" "}
                {result.group.map((g) => g.id).join(", ")}
              </div>
            )}
          </div>
        )}

        {/* Build a combined dispatch (finder + per-line ship qty) */}
        <div style={card} data-testid="dt-combine-builder">
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
            <Truck size={18} weight="fill" color="#7c3aed" /> Dispatch multiple Sales Orders — one delivery per SO
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
            Add the Sales Orders you want to ship, set the ship qty per line, then dispatch. Each SO becomes
            its <b>own outbound delivery</b> (reliable — no cross-SO merging), and every delivery is stamped
            with the same Dispatch / Delivery number. All SOs must share the same ship-from site.
          </div>

          {/* Finder */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <input data-testid="dt-finder-customer" value={finderCust} onChange={(e) => setFinderCust(e.target.value)}
              placeholder="Filter by customer (optional, e.g. AVM)" style={{ ...inp, flex: "1 1 220px", maxWidth: 300 }} />
            <button data-testid="dt-finder-btn" className="jde-btn jde-btn-primary" onClick={findCombinable} disabled={finderLoading}>
              {finderLoading ? "Finding…" : "Find combinable SOs"}
            </button>
            <div style={{ flex: "1 1 160px" }} />
            <input data-testid="dt-add-so-input" value={addSoInput} onChange={(e) => setAddSoInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSoToBuilder(addSoInput)}
              placeholder="…or add SO/PO # directly" style={{ ...inp, width: 200 }} />
            <button data-testid="dt-add-so-btn" className="jde-btn" onClick={() => addSoToBuilder(addSoInput)} disabled={addingSo}
              style={{ whiteSpace: "nowrap" }}>Add</button>
          </div>

          {finderResults && (
            <div data-testid="dt-finder-results" style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #E2E8F0",
              borderRadius: 8, marginBottom: 12 }}>
              {finderResults.length === 0 ? (
                <div style={{ padding: 12, fontSize: 13, color: "#64748b" }}>No combinable SOs in the recent window.</div>
              ) : finderResults.map((s) => {
                const added = builder.some((b) => b.so_id === s.so_id);
                const siteClash = builder[0]?.site && s.site && s.site !== builder[0].site;
                return (
                  <div key={s.so_id} data-testid={`dt-finder-so-${s.so_id}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                      borderBottom: "1px solid #F1F5F9", fontSize: 13, opacity: siteClash ? 0.5 : 1 }}>
                    <span style={{ fontWeight: 700, minWidth: 90 }}>SO {s.so_id}</span>
                    <span style={{ color: "#475569", minWidth: 90 }}>{s.customer || "—"}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#0369a1", background: "#F0F9FF",
                      border: "1px solid #BAE6FD", borderRadius: 999, padding: "1px 8px" }}>{s.site || "?"}</span>
                    <span style={{ color: "#64748b" }}>{s.line_count} line{s.line_count > 1 ? "s" : ""}</span>
                    <div style={{ flex: 1 }} />
                    <button data-testid={`dt-finder-add-${s.so_id}`} className="jde-btn"
                      onClick={() => addSoToBuilder(s.so_id)} disabled={added || siteClash || addingSo}
                      title={siteClash ? `Different site (${s.site}) than the builder (${builder[0].site})` : ""}
                      style={{ fontSize: 12, padding: "5px 12px" }}>
                      {added ? "Added ✓" : siteClash ? "Other site" : "Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Builder tables */}
          {builder.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: "#94a3b8", textAlign: "center",
              border: "1px dashed #CBD5E1", borderRadius: 8 }}>
              No Sales Orders added yet — find and add combinable SOs above.
            </div>
          ) : (() => {
            // Aggregate ship demand vs available stock per product across ALL builder SOs (one site).
            // A product whose combined ship qty exceeds available will make SAP's Goods Issue dump
            // ("Application exception occurred!"), so we flag it and block the combine up-front.
            const shipByProduct = {}, availByProduct = {};
            builder.forEach((b) => b.lines.forEach((l) => {
              const q = Number(l.ship_qty) || 0;
              shipByProduct[l.product] = (shipByProduct[l.product] || 0) + q;
              if (l.available != null) availByProduct[l.product] = l.available;
            }));
            const shortProducts = Object.keys(shipByProduct).filter(
              (p) => availByProduct[p] != null && shipByProduct[p] > availByProduct[p]);
            const isShort = (l) => shortProducts.includes(l.product);
            return (
            <>
              {builder.map((b) => (
                <div key={b.so_id} data-testid={`dt-builder-so-${b.so_id}`} style={{ marginBottom: 12,
                  border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#F8FAFC" }}>
                    <span style={{ fontWeight: 800 }}>SO {b.so_id}</span>
                    <span style={{ color: "#475569", fontSize: 13 }}>{b.customer || "—"}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#0369a1", background: "#F0F9FF",
                      border: "1px solid #BAE6FD", borderRadius: 999, padding: "1px 8px" }}>{b.site || "?"}</span>
                    <div style={{ flex: 1 }} />
                    <button data-testid={`dt-builder-remove-${b.so_id}`} onClick={() => removeSoFromBuilder(b.so_id)}
                      style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                      Remove
                    </button>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", background: "#fff" }}>
                        <th style={{ textAlign: "left", padding: "6px 12px" }}>Item / Product</th>
                        <th style={{ textAlign: "right", padding: "6px 8px" }}>Open</th>
                        <th style={{ textAlign: "right", padding: "6px 8px" }}>Unit price</th>
                        <th style={{ textAlign: "right", padding: "6px 8px" }}>Line total</th>
                        <th style={{ textAlign: "right", padding: "6px 12px" }}>Ship qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.lines.map((l, i) => {
                        const shipN = Number(l.ship_qty);
                        const lineTotal = (!isNaN(shipN) && l.unit_price != null) ? (shipN * l.unit_price) : null;
                        return (
                          <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                            <td style={{ padding: "6px 12px" }}>
                              <div style={{ fontWeight: 700 }}>
                                <span style={{ color: "#94a3b8" }}>#{l.so_item}</span> · {l.product || "—"}
                              </div>
                              <div style={{ color: "#64748b", fontSize: 12 }}>{l.description || ""}</div>
                              {l.available != null && (
                                <div data-testid={`dt-builder-stock-${b.so_id}-${l.so_item}`}
                                  style={{ fontSize: 11, fontWeight: 700, marginTop: 3,
                                    color: isShort(l) ? "#b91c1c" : "#047857" }}>
                                  {isShort(l)
                                    ? `\u26A0 ${availByProduct[l.product]} ${l.unit} free at ${b.site} — need ${shipByProduct[l.product]} · Goods Issue will fail`
                                    : `${l.available} ${l.unit} free at ${b.site}`}
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap" }}>{l.open_qty} {l.unit}</td>
                            <td style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap" }}>
                              {l.unit_price != null ? `${l.unit_price} ${l.currency || ""}` : "—"}</td>
                            <td style={{ textAlign: "right", padding: "6px 8px", whiteSpace: "nowrap" }}>
                              {lineTotal != null ? `${lineTotal.toFixed(2)} ${l.currency || ""}` : "—"}</td>
                            <td style={{ textAlign: "right", padding: "6px 12px" }}>
                              <input data-testid={`dt-builder-qty-${b.so_id}-${l.so_item}`} type="number" min="0" max={l.open_qty} step="1"
                                value={l.ship_qty} onChange={(e) => setBuilderQty(b.so_id, i, e.target.value)}
                                style={{ width: 84, padding: "6px 8px", textAlign: "right", borderRadius: 6,
                                  border: isShort(l) ? "1px solid #dc2626" : "1px solid #cbd5e1",
                                  background: isShort(l) ? "#fef2f2" : "#fff" }} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}

              {(() => {
                const sites = [...new Set(builder.map((b) => b.site).filter(Boolean))];
                const totalLines = builder.reduce((n, b) => n + b.lines.length, 0);
                const cur = builder[0]?.lines?.[0]?.currency || "";
                let grand = 0, hasPrice = false;
                builder.forEach((b) => b.lines.forEach((l) => {
                  const q = Number(l.ship_qty);
                  if (!isNaN(q) && l.unit_price != null) { grand += q * l.unit_price; hasPrice = true; }
                }));
                return (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 13, color: "#475569" }}>
                      {builder.length} SO{builder.length > 1 ? "s" : ""} · {totalLines} line{totalLines > 1 ? "s" : ""} ·
                      site <b>{sites.join(", ") || "?"}</b>
                      {hasPrice && <> · est. total <b>{grand.toFixed(2)} {cur}</b></>}
                    </span>
                    {sites.length > 1 && (
                      <span data-testid="dt-builder-site-warn" style={{ fontSize: 12, color: "#991B1B", fontWeight: 700 }}>
                        Multiple sites — a single delivery must ship from ONE site.
                      </span>
                    )}
                    {shortProducts.length > 0 && (
                      <span data-testid="dt-builder-stock-warn" style={{ fontSize: 12, color: "#991B1B", fontWeight: 700 }}>
                        Not enough stock at {sites.join(", ")} for {shortProducts.join(", ")} — the Goods Issue would fail.
                        Lower the ship qty or ship from a site that has stock.
                      </span>
                    )}
                    <div style={{ flex: 1 }} />
                    <label style={{ fontSize: 12, color: "#475569", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      Dispatch / Delivery #
                      <input data-testid="dt-builder-dispatch-no" value={dispatchNo}
                        onChange={(e) => setDispatchNo(e.target.value)} placeholder="required"
                        style={{ ...inp, width: 170, borderColor: dispatchNo.trim() ? "#cbd5e1" : "#f87171" }} />
                    </label>
                    <button data-testid="dt-builder-combine-btn" onClick={runBuilderCombine}
                      disabled={combining || sites.length > 1 || shortProducts.length > 0 || !dispatchNo.trim()}
                      title={!dispatchNo.trim() ? "Enter a Dispatch / Delivery number first" : "Create one outbound delivery per Sales Order and stamp the dispatch number on each"}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                        padding: "9px 16px", borderRadius: 8, border: "none",
                        background: (combining || sites.length > 1 || shortProducts.length > 0 || !dispatchNo.trim()) ? "#c4b5fd" : "#7c3aed", color: "#fff",
                        cursor: (combining || sites.length > 1 || shortProducts.length > 0 || !dispatchNo.trim()) ? "default" : "pointer" }}>
                      {combining ? "Dispatching…" : "Dispatch SOs · stamp each"}
                    </button>
                  </div>
                );
              })()}
            </>
            );
          })()}
        </div>

        {/* Delivery <-> SO trace report */}
        <div style={card} data-testid="dt-trace">
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
            <MagnifyingGlass size={18} weight="bold" color="#0f766e" /> Delivery ⇄ Sales Order trace
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
            Search a <b>Sales Order</b> to see every combined delivery (each tranche) it produced, or a
            <b> Delivery id</b> to see the Sales Orders &amp; items shipped under it.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "inline-flex", border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
              {["so", "delivery"].map((m) => (
                <button key={m} data-testid={`dt-trace-mode-${m}`} onClick={() => { setTraceMode(m); setTraceResults(null); }}
                  style={{ padding: "8px 14px", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer",
                    background: traceMode === m ? "#0f766e" : "#fff", color: traceMode === m ? "#fff" : "#334155" }}>
                  {m === "so" ? "By Sales Order" : "By Delivery"}
                </button>
              ))}
            </div>
            <input data-testid="dt-trace-input" value={traceQuery} onChange={(e) => setTraceQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runTrace()}
              placeholder={traceMode === "so" ? "Sales Order # e.g. 11938" : "Delivery # e.g. P2D1-5764"}
              style={{ flex: "1 1 220px", minWidth: 180, padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14 }} />
            <button data-testid="dt-trace-btn" onClick={runTrace} disabled={tracing}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700,
                padding: "9px 16px", borderRadius: 8, border: "none",
                background: tracing ? "#94a3b8" : "#0f766e", color: "#fff", cursor: tracing ? "default" : "pointer" }}>
              {tracing ? "Searching…" : "Search"}
            </button>
          </div>
          {traceResults && (
            <div data-testid="dt-trace-results">
              {traceResults.count === 0 ? (
                <div style={{ fontSize: 13, color: "#64748b" }}>No combined shipments found for “{traceResults.query}”.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#64748b", fontSize: 11, textTransform: "uppercase" }}>
                      <th style={{ padding: "6px 8px" }}>Delivery #</th>
                      <th style={{ padding: "6px 8px" }}>Sales Order(s)</th>
                      <th style={{ padding: "6px 8px" }}>Items shipped</th>
                      <th style={{ padding: "6px 8px" }}>Site</th>
                      <th style={{ padding: "6px 8px" }}>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traceResults.results.map((row, i) => (
                      <tr key={row.job_id || i} style={{ borderTop: "1px solid #F1F5F9" }} data-testid={`dt-trace-row-${i}`}>
                        <td style={{ padding: "8px 8px", fontWeight: 800, color: "#0f766e" }}>
                          {row.delivery_id || <span style={{ color: "#b45309", fontWeight: 600 }}>syncing…</span>}
                        </td>
                        <td style={{ padding: "8px 8px" }}>{(row.so_ids || []).join(", ")}</td>
                        <td style={{ padding: "8px 8px" }}>
                          {(row.lines || []).length
                            ? (row.lines || []).map((l, k) => (
                                <span key={k} style={{ display: "inline-block", marginRight: 6, marginBottom: 3, padding: "2px 7px",
                                  borderRadius: 6, background: "#F1F5F9", border: "1px solid #E2E8F0", fontSize: 12 }}>
                                  {l.product || `SO ${l.so} #${l.item}`}{l.qty != null ? ` ×${l.qty}` : ""}
                                </span>
                              ))
                            : <span style={{ color: "#94a3b8" }}>{row.total_lines || 0} line(s)</span>}
                        </td>
                        <td style={{ padding: "8px 8px" }}>{row.site || "—"}</td>
                        <td style={{ padding: "8px 8px", color: "#64748b", fontSize: 12 }}>
                          {row.when ? new Date(row.when).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
